import React from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import PageHero from '../ui/pageHero/PageHero';
import WorkVisual from '../sections/home/work/WorkVisual';
import CtaSection from '../sections/home/cta/CtaSection';
import { fadeUp, viewportOnce } from '../../styles/animations';

const projects = [
    {
        name: 'Financial Platform Transformation',
        tag: 'Digital strategy · Software engineering',
        challenge: 'A mid-sized financial services provider ran core operations across a decade of accumulated systems that no longer scaled with regulatory or customer demands.',
        approach: 'We mapped the platform end to end, prioritized a phased re-architecture, and embedded engineers alongside the client’s team to de-risk delivery.',
    },
    {
        name: 'Digital Customer Experience',
        tag: 'Digital strategy · Product design',
        challenge: 'Fragmented digital touchpoints across channels were creating inconsistent, low-trust customer journeys.',
        approach: 'A unified product strategy and design system brought every channel onto shared foundations, without freezing feature delivery during the transition.',
    },
    {
        name: 'Cloud Infrastructure',
        tag: 'Cloud & infrastructure',
        challenge: 'Growth had outpaced the existing infrastructure, surfacing reliability issues during peak demand.',
        approach: 'We redesigned the infrastructure for multi-region resilience and built the observability practice needed to keep it that way.',
    },
];

const WorkPage = () => (
    <>
        <PageHero
            eyebrow="Selected work"
            title="Case studies"
            subtitle="Concept case studies — illustrative work created for this portfolio project, not real client engagements."
        />

        <Box component="section" sx={{ backgroundColor: 'ink.main', color: 'ink.contrastText', py: { xs: 8, md: 12 }, px: '6%' }}>
            <Box sx={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: { xs: 6, md: 10 } }}>
                {projects.map((p, i) => (
                    <motion.div
                        key={p.name}
                        initial="hidden"
                        whileInView="visible"
                        viewport={viewportOnce}
                        variants={fadeUp}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                gap: { xs: 3, md: 6 },
                                alignItems: 'center',
                            }}
                        >
                            <Box sx={{ position: 'relative', height: 320, borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'lineOnDark' }}>
                                <Box sx={{ position: 'absolute', inset: 0, opacity: 0.9 }}>
                                    <WorkVisual variant={i} />
                                </Box>
                            </Box>
                            <Box>
                                <Box sx={{ display: 'inline-block', border: '1px solid', borderColor: 'lineOnDark', borderRadius: 999, px: 1.5, py: 0.5, mb: 2 }}>
                                    <Typography variant="overline">Concept case study</Typography>
                                </Box>
                                <Typography variant="h2" sx={{ mb: 1 }}>{p.name}</Typography>
                                <Typography variant="body2" sx={{ color: 'fog', mb: 3 }}>{p.tag}</Typography>
                                <Typography variant="body1" sx={{ color: 'fog', mb: 2 }}>{p.challenge}</Typography>
                                <Typography variant="body1" sx={{ color: 'fog' }}>{p.approach}</Typography>
                            </Box>
                        </Box>
                    </motion.div>
                ))}
            </Box>
        </Box>

        <CtaSection />
    </>
);

export default WorkPage;
